import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Store history globally to persist across component unmounts
const navigationHistory = [];
const MAX_HISTORY_LENGTH = 10;

/**
 * Hook to track navigation history
 * @returns {Object} - Navigation history methods
 */
export const useNavigationHistory = () => {
  const location = useLocation();
  const currentPathRef = useRef(null);

  useEffect(() => {
    const fullPath = location.pathname + location.search + location.hash;

    // Only add to history if it's a different path
    if (currentPathRef.current && currentPathRef.current !== fullPath) {
      // Add previous path to history
      navigationHistory.push(currentPathRef.current);

      // Keep history size limited
      if (navigationHistory.length > MAX_HISTORY_LENGTH) {
        navigationHistory.shift();
      }
    }

    // Update current path
    currentPathRef.current = fullPath;
  }, [location]);

  const getPreviousPath = () => {
    // Get the last item from history
    if (navigationHistory.length > 0) {
      return navigationHistory[navigationHistory.length - 1];
    }

    // Default fallback - go one level up in path hierarchy
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 1) {
      pathParts.pop();
      return '/' + pathParts.join('/');
    }

    return '/';
  };

  const clearHistory = () => {
    navigationHistory.length = 0;
  };

  return {
    getPreviousPath,
    clearHistory,
    history: [...navigationHistory]
  };
};

export default useNavigationHistory;
